import { useSearch } from '@faststore/sdk'
import type { Facet, FacetValue } from 'src/components/search/FilterV2/types'
import { PlusIcon } from 'src/components/Icons/PlusIcon'
import { CloseButtonIcon } from 'src/components/Icons/CloseButtonIcon'
import Button from 'src/components/ui/Button'
import IconButton from 'src/components/ui/IconButton'
import SlideOver from 'src/components/ui/SlideOver'
import { useModal } from 'src/sdk/ui/modal/Provider'
import Icon from 'src/components/ui/Icon'
import { useFilter } from 'src/components/search/FilterV2/useFilter'
import Facets from 'src/components/search/FilterV2/Facets'
import { handleAllRemoval } from 'src/components/search/FilterV2/SelectedFacets'
import type { IStoreSelectedFacet } from '@generated/graphql'
import { useCallback, useEffect, useState } from 'react'

import { IconFilterMobile } from '../utils/icons'

type GalleryFilterMobileProps = {
  title: string
  totalCount: number
  facetsv2: Facet[] | undefined
  setIsFilterOpen: React.Dispatch<React.SetStateAction<boolean>>
  isFilterOpen: boolean
}

const GalleryFilterMobile = ({
  title,
  totalCount,
  facetsv2,
  isFilterOpen,
  setIsFilterOpen,
}: GalleryFilterMobileProps) => {
  const [fakeLoading, setFakeLoading] = useState(false)
  const {
    state: { selectedFacets, base, term },
    setFacet,
    setFacets,
    toggleFacet,
  } = useSearch()

  const { onModalClose } = useModal()
  const { facets, selected, expanded, dispatch } = useFilter(facetsv2)

  const facetBrandDecathlon = facetsv2?.find(
    (facet) => facet.key === '77560' || facet.key === 'marca-propria'
  )

  const facetBrandDecathlonValue = facetBrandDecathlon?.values.find(
    (value) => value.value === '77654' || value.value === 'marcas-decathlon'
  )

  const cashbackFacet = facetsv2?.find((facet) => facet.key === 'sellername')
  const cashbackFacetValue = cashbackFacet?.values?.find(
    (values) => values.value === 'iguasport-ltda'
  )

  const handleFilterClick = (facetValue: FacetValue) => {
    if (facetValue.selected) {
      const newFacet = selectedFacets.filter(
        (obj) => obj.key !== facetValue.key
      )

      setFacets(newFacet)
    } else {
      setFacet({
        key: facetValue.key,
        value: facetValue.value,
      })
    }
  }

  function handleModalClose() {
    dispatch({
      type: 'selectFacets',
      payload: selectedFacets,
    })

    onModalClose()
  }

  function handleSetFacet(facet: IStoreSelectedFacet, unique?: boolean) {
    setFakeLoading(true)
    setFacet(facet, unique)
  }

  function handleToggleFacet(facet: IStoreSelectedFacet) {
    setFakeLoading(true)
    toggleFacet(facet)
  }

  useEffect(() => {
    setFakeLoading(false)
  }, [facetsv2])

  const filterFacets = useCallback(() => {
    if (!facetsv2) {
      return []
    }

    return selected
      .map(
        (filter) =>
          facetsv2
            ?.find((facet) => facet.key === filter.key)
            ?.values.find((value) => value.value === filter.value) as FacetValue
      )
      .filter((item) => item?.key !== 'category-1' && item)
  }, [facetsv2, selected])

  const [filteredFacets, setFilteredFacets] = useState<FacetValue[]>(
    filterFacets()
  )

  useEffect(() => {
    setFilteredFacets(filterFacets())
  }, [filterFacets])

  return (
    <div className="w-full">
      <div className="w-full flex items-center justify-center mt-4 mb-6 display-mobile">
        <button
          data-testid="open-filter-button"
          aria-label="Open Filters"
          onClick={() => setIsFilterOpen(!isFilterOpen)}
          className={`h-[48px] flex items-center justify-center rounded-round border gap-2 font-inter text-base font-normal leading-[21px] text-black ${
            filteredFacets.length > 0
              ? 'w-[75px] border-black'
              : 'w-[48px] border-restructure-background-neutral-5'
          }`}
        >
          <IconFilterMobile />
          {filteredFacets.length > 0 ? filteredFacets.length : ''}
        </button>

        <div className="block h-[40px] w-[1px] mx-5 bg-restructure-background-neutral-5" />

        <div className="filter-mobile-scroll-container flex gap-2 flex-1 overflow-scroll">
          {cashbackFacet && cashbackFacetValue && (
            <button
              onClick={() => handleFilterClick(cashbackFacetValue)}
              data-testId={`desktop-${cashbackFacet.key}`}
              className={`flex gap-2 h-[48px] px-4 items-center justify-center border rounded-round min-w-fit ${
                cashbackFacetValue.selected
                  ? 'border-black'
                  : 'border-restructure-background-neutral-5 '
              }`}
            >
              <p className="font-normal text-sm font-inter text-black">
                Promo: 5% OFF no PIX*
              </p>
              {cashbackFacetValue.selected ? (
                <CloseButtonIcon size={20} />
              ) : (
                <PlusIcon width={20} height={20} color="black" />
              )}
            </button>
          )}

          {facetBrandDecathlon && facetBrandDecathlonValue && (
            <button
              onClick={() => handleFilterClick(facetBrandDecathlonValue)}
              data-testId={`desktop-${facetBrandDecathlon.key}`}
              className={`flex gap-2 h-[48px] px-4 items-center justify-center border rounded-round min-w-fit ${
                facetBrandDecathlonValue.selected
                  ? 'border-black'
                  : 'border-restructure-background-neutral-5 '
              }`}
            >
              <p className="font-normal text-sm font-inter text-black">
                Marcas Decathlon
              </p>
              {facetBrandDecathlonValue.selected ? (
                <CloseButtonIcon size={20} />
              ) : (
                <PlusIcon width={20} height={20} color="black" />
              )}
            </button>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between mb-2 ">
        <h2 className="text-base md:text-xl  md:max-w-none flex flex-wrap items-center justify-start gap-1 md:gap-2 capitalize text-restructure-primary font-semibold md:font-medium">
          {title}
          <span
            data-testid="total-product-count"
            data-count={totalCount}
            className=" md:text-sm text-tertiary font-normal"
          >
            ({`${totalCount.toLocaleString()} resultados`})
          </span>
        </h2>
      </div>

      {isFilterOpen && (
        <SlideOver
          isOpen={isFilterOpen}
          onDismiss={() => setIsFilterOpen(false)}
          size="full"
          direction="leftSide"
          className="filter-modal__content"
        >
          <div
            className={`filter-modal__body ${
              filteredFacets.length > 0 ? 'has-filters' : ''
            }`}
          >
            <header className="filter-modal__header">
              <h2 className="title-display flex justify-center items-center">
                <span className="text-xl text-black font-medium">
                  Filtrar produtos
                </span>
              </h2>
              <IconButton
                data-testid="filter-modal-button-close"
                classes="filter-modal__button"
                aria-label="Close Filters"
                icon={<Icon name="X" width={24} height={24} />}
                onClick={() => handleModalClose()}
              />
            </header>
            <Facets
              facetsv2={facets}
              testId="mobile-store-filter"
              indicesExpanded={expanded}
              onFacetChange={(facet, type) =>
                type === 'RANGE'
                  ? handleSetFacet(facet, true)
                  : handleToggleFacet(facet)
              }
              onAccordionChange={(index) =>
                dispatch({ type: 'toggleExpanded', payload: index })
              }
            />
          </div>
          <footer className="filter-modal__footer flex flex-col gap-5">
            {filteredFacets?.length > 0 && (
              <button
                className="text-neutral11 text-sm font-normal underline underline-offset-4"
                onClick={() =>
                  handleAllRemoval(
                    selectedFacets,
                    () => dispatch({ type: 'selectFacets', payload: [] }),
                    { base, term }
                  )
                }
              >
                Limpar filtros ({String(filteredFacets.length).padStart(2, '0')}
                )
              </button>
            )}
            <Button
              variant="primary"
              data-testid="filter-modal-button-apply"
              className="w-full bg-restructure-background-primary-inverted text-restructure-primary-inverted tablet-body-semibold-text2 rounded-round h-12"
              onClick={() => {
                setFacets(selected)
                onModalClose()
              }}
            >
              <div className="flex justify-center items-center">
                {fakeLoading ? (
                  <img
                    src="https://decathlonstoreapp.vtexassets.com/arquivos/loader.gif"
                    alt="decathlon loader"
                    className="max-h-[40px] mix-blend-difference"
                  />
                ) : (
                  `Ver ${totalCount} resultados`
                )}
              </div>
            </Button>
          </footer>
        </SlideOver>
      )}
    </div>
  )
}

export default GalleryFilterMobile
